import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
  type SxProps,
} from "@mui/material";
import { isEmpty } from "lodash-es";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSegmentValueFieldCsvHelper } from "../../../../hooks/use-segment-value-field-csv-helper";
import { useSegmentFieldValueOptions } from "../../../../hooks/use-segment-value-options";
import type { UsableAccountPersonField } from "../../../../server/supabase-segment-field-service";
import type { Condition } from "../../types";
import { RenderInput } from "./autocomplete-select-input";

type Props = {
  condition: Condition;
  segmentField?: UsableAccountPersonField;
  handleFieldChange: (value: string[]) => void;
  errMsg?: string;
  disabled?: boolean;
  hidden?: boolean;
  recipientCondition?: Condition;
  sx?: SxProps;
};

export const AutocompleteValueSelect: FC<Props> = ({
  condition,
  handleFieldChange,
  errMsg: error,
  disabled,
  hidden,
  segmentField,
  recipientCondition,
  sx,
}) => {
  const { data, loading } = useSegmentFieldValueOptions({
    audience: recipientCondition?.field,
    segmentField,
    disabled,
  });

  const selectedFieldOptions = data || [];

  const {
    parsing,
    invalidEmailsDialog,
    handleEntries,
    exportCsvData,
    dropzoneState: { getRootProps, getInputProps, isDragActive, open: openFileUpload },
  } = useSegmentValueFieldCsvHelper({
    options: condition.values,
    allOptions: selectedFieldOptions,
    disabled: !!disabled,
    setOptions: handleFieldChange,
  });

  const isDisabled = disabled || loading || selectedFieldOptions.length === 0;

  return (
    <div {...getRootProps()}>
      <Autocomplete
        multiple
        size="small"
        loading={loading}
        limitTags={isDisabled ? undefined : 3}
        options={selectedFieldOptions}
        hidden={hidden}
        disabled={isDisabled}
        fullWidth
        value={condition.values}
        data-testid="value-autocomplete"
        onChange={(_, value) => {
          handleFieldChange(value ? value.slice() : []);
        }}
        sx={sx}
        renderValue={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const rawTagProps = getTagProps({ index });
            // Remove onDelete from the tagProps if it's disabled
            const { onDelete, ...tagProps } = rawTagProps;

            return (
              <Chip
                {...tagProps}
                onDelete={isDisabled ? undefined : onDelete}
                color={
                  loading || selectedFieldOptions.includes(option) || disabled ? "default" : "error"
                }
                key={option}
                label={option}
                size="small"
              />
            );
          })
        }
        renderInput={(params) => (
          <RenderInput
            params={params}
            isDragActive={isDragActive}
            openFileUpload={openFileUpload}
            exportCsvData={exportCsvData}
            loading={loading}
            parsing={parsing}
            condition={condition}
            handleEntries={handleEntries}
            error={error}
            disabled={disabled}
            segmentField={segmentField}
            getInputProps={getInputProps}
          />
        )}
      />
      {invalidEmailsDialog.open && invalidEmailsDialog.data && (
        <InvalidEmailsDialog
          valid={invalidEmailsDialog.data.valid}
          invalid={invalidEmailsDialog.data.invalid}
          duplicates={invalidEmailsDialog.data.duplicates}
          error={invalidEmailsDialog.data.error}
          onClose={invalidEmailsDialog.handleClose}
          onSubmit={(valid) => {
            invalidEmailsDialog.handleClose();
            handleFieldChange(valid);
          }}
        />
      )}
    </div>
  );
};

const InvalidEmailsDialog: FC<{
  valid: string[];
  invalid: string[];
  duplicates: Record<string, string[]>;
  error?: string;
  onClose: () => void;
  onSubmit: (valid: string[]) => void;
}> = ({ valid, invalid, duplicates, error, onClose, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{t("There was a problem uploading your data")}</DialogTitle>
      <DialogContent>
        {error && <Typography>{error}</Typography>}
        {invalid.length > 0 && (
          <>
            <Typography variant="subtitle1">{t("The following entries are invalid")}</Typography>
            <List>
              {invalid.map((entry, i) => (
                <ListItem key={i}>{entry}</ListItem>
              ))}
            </List>
          </>
        )}
        {!isEmpty(duplicates) && (
          <>
            <Typography variant="subtitle1">{t("The following entries are duplicated")}</Typography>
            <List>
              {Object.entries(duplicates).map(([key, values], i) => (
                <ListItem key={i}>
                  {key}: {values.join(", ")}
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="segment-form-upload-error-dialog-cancel"
          onClick={onClose}
          color="warning"
        >
          {t("Cancel")}
        </Button>
        {!error && (
          <Button
            data-analytics-id="segment-form-upload-error-dialog-continue"
            onClick={() => onSubmit(valid)}
            color="info"
          >
            {t("Continue")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
